import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { waitUntilDbIsOnline } from "utils";

/** @type {import("types/commonTypes").NewSenderData}*/
const defaultFormData = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  isAgreeToTerms: false,
  code: "",
  captchaId: "",
  recipientId: "",
  practiceId: "",
  originatorId: "",
};

export const useSignUpSender = ({
  originator,
  showError,
  providers,
  setIsDataChanged,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUpDone, setIsSignUpDone] = useState(false);
  const [currentRecipient, setCurrentRecipient] = useState({
    id: "",
    name: "",
  });
  const [selectedProviders, setSelectedProviders] = useState([]);

  const { control, handleSubmit, watch, setError, setValue } = useForm({
    defaultValues: defaultFormData,
  });

  const setSelectedRecipient = ({ id, name }) => {
    setCurrentRecipient({ id, name });
    setValue("recipientId", id, { shouldDirty: true });
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    const isOnLine = await waitUntilDbIsOnline();

    if (!isOnLine) {
      setError("No connection to cloud. Try again later.");
      setIsLoading(false);
      return;
    }

    const seletedProviderIds = Object.keys(selectedProviders).filter((id) =>  selectedProviders[id].selected);

    const newData = {
      ...data,
      originatorId: originator?.id,
      practiceId: originator?.organizationId,
      providers: seletedProviderIds
    };

    newData.firstName = newData.firstName.trim();
    newData.lastName = newData.lastName.trim();

    let result;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/create_sender`,
        {
          method: "PUT",
          body: JSON.stringify(newData),
        }
      );
      result = await response.json();
    } catch (error) {
      showError(error?.message ?? "Something went wrong");
      setIsLoading(false);
      return;
    }

    if (result?.error) {
      if (result?.error === "Invalid code") {
        setError("code", {
          message: "Invalid code",
        });
        setIsLoading(false);
        return;
      }
      showError(result?.error);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setIsSignUpDone(true);
  };

  const handleChangeProvider = (e, id, label) => {
    setIsDataChanged(true);

    const unckeckedProviders = Object.keys(selectedProviders)
      .map((providerId) => ({
        [providerId]: {
          label: selectedProviders[providerId].label,
          selected: false,
        },
      }))
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});

    const newValue = {
      ...unckeckedProviders,
      [id]: {
        label,
        selected: true,
      },
    };
    setSelectedProviders(newValue);
  };

  const recipientId = watch("recipientId");
  const isProviderSelected = Object.values(selectedProviders).some(
    (provider) => provider.selected
  );

  useEffect(() => {
    const providersForSelector = providers
      .map(({ id, name, speciality }) => ({
        [id]: {
          label: [name, speciality].filter(Boolean).join(" - "),
          selected: selectedProviders[id]?.selected ?? false,
        },
      }))
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});
    setSelectedProviders(providersForSelector);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    control,
    setValue,
    currentRecipient,
    setSelectedRecipient,
    handleSubmit,
    onSubmit,
    recipientId,
    isSignUpDone,
    isLoading,
    selectedProviders,
    isProviderSelected,
    handleChangeProvider,
  };
};
